import type { FragmentType } from '@packages/gql/generated/shopping';
import type { SearchProductCardItemFragmentFragmentDoc } from '@packages/gql/generated/shopping/SearchProductCardItemFragmentFragmentDoc';
import type { SearchProductCardResultFragmentFragmentDoc } from '@packages/gql/generated/shopping/SearchProductCardResultFragmentFragmentDoc';
import { unmask } from '@packages/gql/src/betterMasking';
import { ProductCardV2Brand } from '@packages/modules/src/ProductCardV2/ProductCardV2.Brand';
import { ProductCardV2Name } from '@packages/modules/src/ProductCardV2/ProductCardV2.Name';
import type { SearchProductCardFullVariationGroupFragmentFragmentDoc } from '@packages/gql/generated/shopping/SearchProductCardFullVariationGroupFragmentFragmentDoc';
import type { ProductListPriceFragmentFragmentDoc } from '@packages/gql/generated/shopping/ProductListPriceFragmentFragmentDoc';
import { useDisplayModeValue } from '../../hooks/useDisplayMode';
import { useHrefWithPageNumber } from '../../hooks/useHrefWithPageNumber';

export type SearchProductCardPlaceholderContentProps = {
  maskedResult: FragmentType<typeof SearchProductCardResultFragmentFragmentDoc>;
  maskedItem: FragmentType<typeof SearchProductCardItemFragmentFragmentDoc>;
  pageNumber: number;
};

export const SearchProductCardPlaceholderContent = ({
  maskedResult,
  maskedItem,
  pageNumber,
}: SearchProductCardPlaceholderContentProps) => {
  const result = unmask<typeof SearchProductCardResultFragmentFragmentDoc>(maskedResult);
  const item = unmask<typeof SearchProductCardItemFragmentFragmentDoc>(maskedItem);
  const primaryVariationGroup = unmask<
    typeof SearchProductCardFullVariationGroupFragmentFragmentDoc
  >(item.primaryVariationGroup);
  const price = unmask<typeof ProductListPriceFragmentFragmentDoc>(primaryVariationGroup.price);

  const displayMode = useDisplayModeValue(result);

  // TODO SEARCH-3513 check if it is a problem that probably no tracking and stuff happens when clicking the placeholder, even if it links to the detail view; may not be an issue because it's just a fallback in case the card is not rendered properly, or way too late; In these cases, it's probably preferable that the user can click the card at all, and get to the detail view, even if it doesn't track anything;
  const href = useHrefWithPageNumber(primaryVariationGroup.href, pageNumber);
  // TODO FETW-617 fancify and maybe use proper Skeleton components from shared/ui

  const imageOrientation = primaryVariationGroup.image.includes('$PL_landscape_hq$')
    ? 'landscape'
    : 'portrait';
  const simplifiedDisplayMode =
    displayMode === 'cards' || displayMode === 'largeCards' ? 'cards' : 'other';

  const heightClass = {
    'cards-portrait': 'h-[300px]',
    'cards-landscape': 'h-[123px]',
    'other-portrait': 'h-[450px]',
    'other-landscape': 'h-[168px]',
  }[`${simplifiedDisplayMode}-${imageOrientation}`];

  return (
    <>
      {/* Image etc */}
      <div
        className={`product-card-image bg-color-grey-light [grid-area:1/2/span_1/span_1] ${heightClass}`}
      />

      {/* brand and name same as normal content, to be rudimentary usable */}
      <ProductCardV2Brand id={item.styleId}>{item.brand.name}</ProductCardV2Brand>
      <ProductCardV2Name id={item.styleId} href={href}>
        {item.styleName}
      </ProductCardV2Name>

      {/* price */}
      <div
        className="bg-color-grey-light mx-2 mt-1 [grid-area:4/2/span_5/span_1]"
        style={{
          height: `${28 + (price.discount ? 16 : 0) + (price.unitPackage ? 16 : 0)}px`,
        }}
      />
      {/* color buttons, should be always visible */}
      <div className="bg-color-grey-light mx-2 mt-1 h-[30px] [grid-area:9/2/span_1/span_1]" />
      {/* extended description */}
      {displayMode === 'moreInformation' && (
        <div className="product-card-extended-description bg-color-grey-light mx-2 mt-1 h-[50px] [grid-area:10/2/span_1/span_1]" />
      )}
      {/* rating */}
      {item.ratings && (
        <div className="bg-color-grey-light mx-2 mt-1 h-[16px] [grid-area:11/2/span_1/span_1]" />
      )}
      {/* efficiency flags, assuming a single row */}
      {item.efficiencyFlags && (
        <div className="bg-color-grey-light mx-2 mt-1 h-[41px] [grid-area:12/2/span_1/span_1]" />
      )}
    </>
  );
};
